import React from 'react'
import { Box, Button, Typography, Tooltip, Link } from '@mui/material'
import { OpenInNewOffOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { NAVY, SUBTLE, BORDER, WHITE } from '../../../styles/foundation/colors'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'

const btnSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['2xs'],
  textTransform: 'none',
  borderColor: BORDER,
  color: NAVY,
  '&:hover': { borderColor: SUBTLE },
}

export default function DocumentsBar({
  orderDetails,
  onViewLabel,
  onViewInvoice,
  onViewLRCopy,
  onFetchLabel,
  isFetchingLabel,
}) {
  if (!orderDetails) return null

  const isReverseShipment = orderDetails.order_type === 'REVERSE SHIPMENT'
  const isB2B = orderDetails.shipment_type === 'B2B'
  const isTrackOnly = orderDetails.isTrackOnlyOrder
  const hasBeenPickedUp = !!orderDetails.actualPickUpTime

  const showLabel = !isReverseShipment && !isTrackOnly && !hasBeenPickedUp
  const showInvoice = !hasBeenPickedUp
  const showLRCopy = isB2B && orderDetails.label_url && orderDetails.label_url !== ''
  const showRefetchLabel =
    isB2B &&
    !orderDetails.b2bPackageLabelUrl &&
    !isTrackOnly &&
    orderDetails.courierDetail?.prozoId?.includes('DELHIVERY_PRZ_INT')
  const showManifest = !!orderDetails.manifestDetails

  const labelUrl = isB2B
    ? orderDetails.b2bPackageLabelUrl || null
    : orderDetails.label_url || null
  const lrUrl = isB2B ? orderDetails.label_url : orderDetails.b2bPackageLabelUrl

  const hasAnyDoc = showLabel || showInvoice || showLRCopy || showRefetchLabel || showManifest || (isTrackOnly && isB2B)

  if (!hasAnyDoc) return null

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: `1px solid ${BORDER}`,
        bgcolor: WHITE,
        p: 2,
        mb: 2,
        display: 'flex',
        gap: 1.5,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY, mr: 0.5 }}
      >
        Documents
      </Typography>

      {showLabel && (
        <Tooltip title='Link expires after 7 days of generation'>
          <Button
            variant='outlined'
            size='small'
            startIcon={<OpenInNewOffOutlined fontSize='small' />}
            onClick={() => onViewLabel(labelUrl)}
            sx={btnSx}
          >
            View Label
          </Button>
        </Tooltip>
      )}

      {showInvoice && (
        <Tooltip title='Link refreshes after 7 days of generation'>
          <Button
            variant='outlined'
            size='small'
            startIcon={<OpenInNewOffOutlined fontSize='small' />}
            onClick={() => onViewInvoice(orderDetails.invoice_url || null, 'INVOICE')}
            sx={btnSx}
          >
            View Invoice
          </Button>
        </Tooltip>
      )}

      {showLRCopy && (
        <Tooltip title='Link expires after 7 days of generation'>
          <Button
            variant='outlined'
            size='small'
            startIcon={<OpenInNewOffOutlined fontSize='small' />}
            onClick={() => onViewLRCopy(lrUrl)}
            sx={btnSx}
          >
            LR Copy
          </Button>
        </Tooltip>
      )}

      {showManifest && (
        <>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
            Manifest: {orderDetails.manifestDetails.manifestNumber}
          </Typography>
          <Button
            variant='outlined'
            size='small'
            component={Link}
            href={orderDetails.manifestDetails.manifestUrl}
            target='_blank'
            startIcon={<OpenInNewOffOutlined fontSize='small' />}
            sx={btnSx}
          >
            View Manifest
          </Button>
        </>
      )}

      {showRefetchLabel && (
        <LoadingButton
          loading={isFetchingLabel}
          variant='contained'
          size='small'
          onClick={() => onFetchLabel(orderDetails.reference)}
          color='primary'
          sx={{ ...btnSx, color: WHITE, '&:hover': { filter: 'brightness(0.9)' } }}
        >
          Refetch Label
        </LoadingButton>
      )}

      {isTrackOnly && isB2B && (
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, fontStyle: 'italic' }}>
          Track-only order — shipping label not available
        </Typography>
      )}
    </Box>
  )
}
